import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { TextLegacy } from '@/components/common'
import { NewCol } from '@/components/common/layouts/NewCol'
import { NewRow } from '@/components/common/layouts/NewRow'
import { ModalNotice } from '@/components/common/modals'
import { BulletedText } from '@/components/common/paragraphs'
import { Switch } from '@/components/common/ui/switch'
import { LayoutHeader } from '@/components/domains/layouts'
import { FieldKeysEnum } from '@/constants/field-keys.enum'
import { GlobalColorEnum } from '@/constants/global-color.enum'
import { useTracker } from '@/containers/contexts/EventTrackingProvider'
import MUTATION_VARIABLES from '@/containers/gqls/base/mutation_variables'
import { useApiMutation, useModal } from '@/containers/hooks'
import { UserContainer } from '@/containers/users/UserContainer'
import { FitpetMallEventEnum } from '@/utils/event-tracker/events'
import { nowDate } from '@/utils/utilDate'

const PageMyPageMarketingAgreement: FC = () => {
  const tracker = useTracker()
  const [agreeMarketing, toggleAgreeMarketing] = useState<boolean>(false)
  const [agreeNightMarketing, toggleAgreeNightMarketing] = useState<boolean>(false)

  const { useModalProps } = useModal()
  const { me, refreshUserInfo } = UserContainer.useContainer()

  useEffect(() => {
    toggleAgreeMarketing(!!me?.agreeMarketing)
    toggleAgreeNightMarketing(!!me?.agreeNightMarketing)
  }, [me])

  const [UpdateAgreeTermsMutation] = useApiMutation('UpdateAgreeTerms')

  const update = async (name: string, checked: boolean) => {
    const oldAgreeMarketingChecked = agreeMarketing
    const oldAgreeNightMarketingChecked = agreeNightMarketing

    let modalTitleText: string
    let followField

    // 마케팅수신
    if (FieldKeysEnum.AgreeMarketing === name) {
      toggleAgreeMarketing(checked)
      modalTitleText = checked ? '수신 동의 처리 완료' : '수신 거부 처리 완료'

      // 마케팅 수신 거부시 야간도 같이 거부
      if (!checked) {
        toggleAgreeNightMarketing(checked)
        followField = {
          [FieldKeysEnum.AgreeNightMarketing]: checked,
        }
      }

      followField = {
        ...followField,
        [FieldKeysEnum.AgreePrMarketing]: checked,
      }

      // 야간 마케팅 수신
    } else {
      toggleAgreeNightMarketing(checked)
      modalTitleText = checked ? '야간 혜택 수신 동의 처리 완료' : '야간 혜택 수신 거부 처리 완료'

      // 야간 마케팅 동의시 마케팅도 같이 동의
      if (checked) {
        toggleAgreeMarketing(checked)
        followField = {
          [FieldKeysEnum.AgreeNightMarketing]: checked,
          [FieldKeysEnum.AgreeMarketing]: checked,
          [FieldKeysEnum.AgreePrMarketing]: checked,
        }
      }
    }
    let subText: string | undefined = checked
      ? `전송자 : 핏펫\n수신 동의 일시 : ${nowDate()}\n마이페이지 > 내정보에서 변경 가능`
      : `전송자 : 핏펫\n수신 거부 일시 : ${nowDate()}\n마이페이지 > 내정보에서 변경 가능`

    const values = {
      ...followField,
      id: me?.id,
      [name]: checked,
    }

    const variables = MUTATION_VARIABLES.UPDATE_AGREE_TERMS(values)
    // @ts-ignore
    const result = await UpdateAgreeTermsMutation({ variables })

    if (result.errors) {
      toggleAgreeMarketing(oldAgreeMarketingChecked)
      toggleAgreeNightMarketing(oldAgreeNightMarketingChecked)
      modalTitleText = '네트워크 연결이 불안정합니다.\n잠시 후 다시 시도해 주세요.'
      subText = undefined
    }
    refreshUserInfo()

    useModalProps.setActionParams({
      noticeText: modalTitleText,
      subText: subText,
    })
    useModalProps.showModal()
  }

  if (!me) {
    return null
  }

  return (
    <>
      <LayoutHeader />
      <RenderListRow
        title="이벤트 및 혜택 알림"
        subTitle="할인 쿠폰, 이벤트, 기획전 등 다양한 정보를 빠르게 받아 보실 수 있습니다."
        defaultChecked={agreeMarketing}
        onChange={(checked: boolean) => {
          tracker.setUserProperties({ marketing_allowed: checked, agree_pr_marketing: checked })
          tracker.triggerCustomEvent(FitpetMallEventEnum.ClickMypageMarketingAgreement, {
            marketingAgreement: checked,
          })
          update(FieldKeysEnum.AgreeMarketing, checked)
        }}
      />
      <RenderListRow
        title="야간 혜택 알림"
        subTitle="밤 9시~ 아침 8시에 혜택을 받아 보실 수 있습니다."
        defaultChecked={agreeNightMarketing}
        onChange={(checked: boolean) => {
          tracker.setUserProperties({ marketing_night_allowed: checked })
          tracker.triggerCustomEvent(FitpetMallEventEnum.ClickMypageMarketingNightAgreement, {
            marketingNightAgreement: checked,
          })
          update(FieldKeysEnum.AgreeNightMarketing, checked)
        }}
      />
      <GreySection>
        <NewRow style={{ paddingBottom: 8 }}>
          <BulletedText
            textStyle={{
              size: { xs: 12, md: 14 },
              color: 'gray400',
            }}
            text="주문 정보 관련 안내, 회사의 주요 정책 변경, 법적 고지 정보는 마케팅(광고) 정보 수신 동의 여부와 관계없이 발송될 수 있습니다."
          />
        </NewRow>
        <BulletedText
          textStyle={{
            size: { xs: 12, md: 14 },
            color: 'gray400',
          }}
          text="마케팅(광고) 정보 수신 거부를 하더라도 시스템 적용으로 인해 수신 거부 일로부터 2~3일간 알람을 받으실 수 있습니다."
        />
      </GreySection>

      <ModalNotice useModalProps={useModalProps} />
    </>
  )
}

const RenderListRow = ({
  title,
  subTitle,
  defaultChecked,
  onChange,
}: {
  title: string
  subTitle: string
  defaultChecked: boolean
  onChange: (checked: boolean) => void
}) => {
  return (
    <ListRow>
      <NewCol flex={1}>
        <ListTitleRow justify="space-between" align="center">
          <TextLegacy>{title}</TextLegacy>
          <Switch checked={defaultChecked} onChange={(checked) => onChange(checked)} />
        </ListTitleRow>
        <NewRow>
          <TextLegacy color="gray400" size={{ xs: 12, md: 14 }}>
            {subTitle}
          </TextLegacy>
        </NewRow>
      </NewCol>
    </ListRow>
  )
}

const ListRow = styled(NewRow)`
  padding: 20px;
  border-bottom: 1px solid ${GlobalColorEnum.Gray70};
`

const ListTitleRow = styled(NewRow)`
  margin-bottom: 16px;
`

const GreySection = styled.div`
  padding: 20px;
  background-color: ${(props) => props.theme.color.gray50};
`

export default PageMyPageMarketingAgreement
